import axios from "axios";
import md5 from "md5"; // Import the md5 hashing library
import EmployeeModel from "models/employeeModel";
import { Client } from "@stomp/stompjs";
import { saveAs } from "file-saver";
import { allowedTenantIds, getTenantKey } from "./utils";
import { id } from "date-fns/locale";

//URLs .
const baseURL = "https://hrhealthone.up.railway.app";
// const baseURL = "http://localhost:8080";

export var API = axios.create({
  baseURL: baseURL + "/api/v1",
});

// Example API calls
export const loginRequest = async (username, password, rememberMe) => {
  try {
    let tenant = "";

    if (!password) {
      return "Please enter a password";
    }
    // Split the username on the hyphen
    const [tenantId, id] = username.split("-");

    if (!allowedTenantIds.includes(tenantId.toUpperCase())) {
      return "Invalid ID";
    }
    tenant = getTenantKey(tenantId);
    API.defaults.headers["X-Tenant-ID"] = tenant;

    // Hash the password using md5
    const hashedPassword = md5(password); // MD5 hash of the password
    var output = await API.post("/employee/login", {
      id,
      password: hashedPassword,
    });

    API.defaults.headers["Authorization"] = "Bearer " + output.data.token;
    // Save token in localStorage after login
    localStorage.setItem("authToken", output.data.token);
    localStorage.setItem("tenant", tenant);

    if (output && output.data && output.data.employeeModel) {
      const employee = new EmployeeModel(output.data.employeeModel);
      return employee;
    }
  } catch (e) {
    return "Invalid username or password";
  }
};

export const logoutRequest = () => {
  delete API.defaults.headers["X-Tenant-ID"];
  delete API.defaults.headers["Authorization"];
  localStorage.removeItem("tenant");
  localStorage.removeItem("authToken");
};

export const checkLoggedIn = async () => {
  try {
    const tenant = localStorage.getItem("tenant");
    const token = localStorage.getItem("authToken");
    if (token) {
      const response = await API.get("/employee/me", {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-ID": tenant },
      });
      if (response.status == 500) {
        return;
      }
      API.defaults.headers["X-Tenant-ID"] = tenant;
      API.defaults.headers["Authorization"] = "Bearer " + token;
      const employee = new EmployeeModel(response.data);
      return employee;
    }
    return "error";
  } catch (e) {
    return "error";
  }
};

export const getTopDashboardLayer = async () => {
  try {
    const response = await API.get("/employee/get-count-for-all");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPieChartData = async (filter) => {
  try {
    let response = await API.get("/employee/pie-chart-data");

    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPerformanceChartData = async (year, month, filter, employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/find-result-by-filter", {
      params: {
        year,
        month,
        filter,
        employeeId,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEmployeePerformanceDistribution = async (month, year, jobId) => {
  try {
    const response = await API.get(`/kpi_assignments/find-results-by-month-year`, {
      params: { month, year, jobId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getTopDashboardUser = async (month, year, departmentId, employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/top-cards-user", {
      params: { month, year, departmentId, employeeId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getKpiNames = async () => {
  try {
    const response = await API.get("/kpi_names");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addKpiName = async (kpi) => {
  try {
    const response = await API.post("/kpi_names", kpi);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteKpiName = async (kpiNameId) => {
  try {
    const response = await API.delete("/kpi_names/" + kpiNameId);
    return response.status;
  } catch (e) {
    return "error";
  }
};

//JOB DATA

export const getJobs = async () => {
  try {
    const response = await API.get("/job");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addJob = async (job) => {
  try {
    const response = await API.post("/job", job);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteJob = async (jobId) => {
  try {
    const response = await API.delete("/job/" + jobId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEmployeesByJobId = async (jobId) => {
  try {
    const response = await API.get("/employee/find-employees-by-job-id/" + jobId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

//END JOB DATA

export const getCompanies = async () => {
  try {
    const response = await API.get("/company");
    return response.data;
  } catch (e) {
    return "error";
  }
};

//EMPLOYEE DATA

export const getEmployees = async () => {
  try {
    const response = await API.get("/employee");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEmployeesPaging = async (
  page,
  size,
  jobId,
  branchId,
  role,
  search,
  filterNoJob,
  filterNoBranch,
  filterNoReference,
  filterNoDirectManager
) => {
  try {
    const response = await API.get("/employee/paging", {
      params: {
        page,
        size,
        jobId,
        branchId,
        role,
        search,
        filterNoJob,
        filterNoBranch,
        filterNoReference,
        filterNoDirectManager,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEmployee = async (id) => {
  try {
    const response = await API.get("/employee/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateEmployee = async (employee) => {
  try {
    const response = await API.put("/employee/" + employee.id, employee);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addEmployee = async (employee) => {
  try {
    const response = await API.post("/employee", employee);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteEmployee = async (id) => {
  try {
    // await API.delete("/employee/" + id);
    const res = await API.delete(`/employee/${id}`);
    return res.data;
  } catch (e) {
    return "error";
  }
};

export const getPhoto = async (id) => {
  try {
    const response = await API.get("/employee/get_photo/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// END EMPLOYEE DATA

//KPI ASSIGNMENT DATA

export const getKpiAssignments = async () => {
  try {
    const response = await API.get("/kpi_assignments");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const saveKpiAssignment = async (kpi) => {
  try {
    const response = await API.post("/kpi_assignments", kpi);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getKpiPerformance = async () => {
  try {
    const response = await API.get("/performance");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteKpiAssignment = async (kpiAssignmentId) => {
  try {
    const response = await API.delete("/kpi_assignments/" + kpiAssignmentId);
    return response.status;
  } catch (e) {
    return "error";
  }
};

export const saveAllKpiAssignmentChanges = async (kpiAssignments) => {
  try {
    const res = await API.post("/kpi_assignments/save-all", kpiAssignments);
    return res.data;
  } catch (e) {
    return "error";
  }
};

export const getMyScorecard = async (employeeId, jobId, month, year) => {
  try {
    const response = await API.get("/kpi_assignments/get_my_scorecard", {
      params: {
        employeeId,
        job_id: jobId,
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getKpisForStrategicRoadmap = async (employeeId, month, year) => {
  try {
    const response = await API.get("/kpi_assignments/strategic_roadmap_data", {
      params: {
        employeeId,
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getMyQuarterlyScorecard = async (employeeId, quarter, year) => {
  try {
    const response = await API.get("/kpi_assignments/get_my_quarterly_scorecard", {
      params: {
        employeeId,
        quarter,
        year,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

//END KPI ASSIGNMENT DATA

//BRANCH DATA
export const getBranches = async () => {
  try {
    const response = await API.get("/branch");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addBranch = async (branch) => {
  try {
    const response = await API.post("/branch", branch);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteBranch = async (branchId) => {
  try {
    const response = await API.delete("/branch/" + branchId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

//END BRANCH DATA

export const updateCompanyDetails = async (company) => {
  try {
    const response = await API.post("/company", company);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// DEPARTMENT DATA

export const getDepartments = async () => {
  try {
    const response = await API.get("/department");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addDepartment = async (departmentModel) => {
  try {
    const response = await API.post("/department", departmentModel);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteDepartment = async (departmentId) => {
  try {
    const response = await API.delete("/department/" + departmentId);
    return response;
  } catch (e) {
    if (e.response && e.response.status === 409) {
      // Backend sent a conflict (department linked to jobs)
      return { error: e.response.data }; // ✅ return the message
    }
    return { error: "error" }; // fallback
  }
};

//END DEPARTMENT DATA

export const getJobToBranch = async () => {
  try {
    const response = await API.get("/job_to_branch");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateBranchJobs = async (branchToJobs) => {
  try {
    const response = await API.post("/job_to_branch/update-all", branchToJobs);
  } catch (e) {
    return "error";
  }
};

export const updateJobBranches = async (jobToBranches) => {
  try {
    const response = await API.post("/job_to_branch/update-all-jobs", jobToBranches);
  } catch (e) {
    return "error";
  }
};

export const updateApprovementStatus = async (employeeId, kpiId, month, year, value, isTarget) => {
  try {
    const response = await API.post("/performance/update_approvement", {
      e_id: employeeId,
      kpi_id: kpiId,
      month,
      year,
      value,
      is_target: isTarget,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updatePerformance = async (
  employeeId,
  kpiId,
  month,
  year,
  target,
  achievement,
  primary,
  selfAchievement
) => {
  try {
    const response = await API.post("/performance/update_performance_values", {
      e_id: employeeId,
      kpi_id: kpiId,
      month,
      year,
      target,
      achievement,
      primary,
      selfAchievement,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getKpiAssignmentsForEmployee = async (employeeId, assessor_id, month, year) => {
  try {
    const response = await API.get("/kpi_assignments/kpiAssignments", {
      params: { employeeId, month, year, assessor_id },
    });

    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getSecondaryKpiAssignmentsForEmployee = async (
  employeeId,
  assessor_id,
  month,
  year
) => {
  try {
    const response = await API.get("/kpi_assignments/kpis-for-secondary", {
      params: { employeeId, month, year, assessor_id },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEmployeesForPrimaryAssessor = async (assessor_id) => {
  try {
    const response = await API.get("/kpi_assignments/get-emps-for-assessor_web", {
      params: { assessor_id },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEmployeesForSecondaryAssessor = async (assessor_id) => {
  try {
    const response = await API.get("/kpi_assignments/get-emps-for-secondary-assessor_web", {
      params: { assessor_id },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getKpisHistory = async (employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/get-kpi-history", { params: { employeeId } });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPerformanceHistory = async (employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/get-weighed-history", {
      params: { employeeId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getDepartmentById = async (departmentId) => {
  try {
    const response = await API.get("/department/" + departmentId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getMyTeam = async (month, year, employeeId, isQuarterly) => {
  try {
    const response = await API.get("department/get_my_team_web", {
      params: { month, year, e_id: employeeId, isQuarterly },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getIndividualForMyTeam = async (month, year, employeeId, isQuarterly) => {
  try {
    const response = await API.get("department/get_individual_for_my_team_web", {
      params: { month, year, e_id: employeeId, isQuarterly },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getTeamData = async (month, year, employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/get_team_analysis", {
      params: { month, year, e_id: employeeId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getCommonKpis = async (month, year, employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/get_common_kpis", {
      params: { month, year, e_id: employeeId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getScorecardExport = async (id, value, year, employeeName, isMonth) => {
  try {
    const response = await API.get("/employee/export-scorecard", {
      params: { id, value, year, isMonth },
      responseType: "blob",
    });

    // Get filename from Content-Disposition header (if available)
    const contentDisposition = response.headers["content-disposition"];
    let filename = "scorecard.xlsx"; // Default filename
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+?)"/);
      if (match) {
        filename = match[1];
      }
    }

    // Trigger the file download
    const suffix = isMonth ? `${value}_${year}` : `Q${value}_${year}`;
    saveAs(response.data, `${employeeName.trim()}_${suffix}.xlsx`);
  } catch (e) {
    return "error";
  }
};

export const getCompanyById = async (companyId) => {
  try {
    const response = await API.get(`/company/${companyId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateCompany = async (company) => {
  try {
    const response = await API.post("/company", company);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAreas = async () => {
  try {
    const response = await API.get("/area");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addArea = async (area) => {
  try {
    const response = await API.post("/area", area);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteArea = async (areaId) => {
  try {
    const response = await API.delete("/area/" + areaId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getBranchPerformance = async (year, month, branchId) => {
  try {
    const response = await API.get("/kpi_assignments/find-result-by-branch", {
      params: { year, month, branchId },
    });

    return response.data;
  } catch (e) {
    console.error(e);
    return "error";
  }
};

export const getOrCreateChatRoom = async (performanceId, employeeId, isTarget) => {
  try {
    const response = await API.post("/chat-rooms/create", null, {
      params: {
        performanceId,
        employeeId,
        isTarget,
      },
    });

    return response.data;
  } catch (e) {
    return "error";
  }
};
export const getInitiativesByCompanyId = async (companyId) => {
  try {
    const response = await API.get(`/company-initiatives/company/${companyId}`);

    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getFABChatData = async (employeeId) => {
  try {
    const response = await API.get(`/chat-rooms/user/${employeeId}`);
    return response.data;
  } catch (error) {
    return "error";
  }
};
export const updateCompanyData = async (id, companyData) => {
  try {
    const response = await API.put(`/company/${id}`, companyData);
    return response.data;
  } catch (e) {
    console.error("Error updating company:", e);
    return "error";
  }
};

export const deleteInitiativeById = async (id) => {
  try {
    const response = await API.delete(`/company-initiatives/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addInitiative = async (initiativeData) => {
  try {
    const response = await API.post("/company-initiatives", initiativeData);
    return response.data;
  } catch (e) {
    // console.error(`Error adding initiative with ID ${id}:`, e);
    // console.error("Server responded with:", error.response.data); // ✅ Show API error
    return "error";
  }
};

export const updateInitiative = async (initiative) => {
  try {
    const response = await API.put("/company-initiatives/" + initiative.initiativeId, initiative);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteValueById = async (id) => {
  try {
    const response = await API.delete(`/company-values/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateValue = async (value) => {
  try {
    const response = await API.put("/company-values/" + value.valueId, value);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getValuesByCompanyId = async (companyId) => {
  try {
    const response = await API.get(`/company-values/company/${companyId}`);

    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addValue = async (valueData) => {
  try {
    const response = await API.post("/company-values", valueData);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getOldMessagesByChatRoom = async (chatRoomId, employeeId) => {
  try {
    const response = await API.get("/chat-messages/chat-room/" + chatRoomId, {
      params: { employeeId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateSelfAchievement = async (performanceId, selfAchievement) => {
  try {
    // const response = await API.put(`/self-achievement/${performanceId}`, { selfAchievement });
    const response = await API.put(`/performance/self-achievement/${performanceId}`, {
      selfAchievement,
    });
    return response.data;
  } catch (e) {
    console.error("API Request Failed:", e.response?.data || e.message);
    return "error";
  }
};

export const createNewStompClient = (chatRoomId) => {
  try {
    const token = API.defaults.headers["Authorization"].split(" ")[1];
    const tenant = API.defaults.headers["X-Tenant-ID"];
    const stompClient = new Client({
      brokerURL: `${baseURL}/ws-chat`, // Use ws:// or wss:// for WebSockets
      connectHeaders: {
        Authorization: token,
        "X-Tenant-ID": tenant,
      },
      reconnectDelay: 5000,
    });

    return stompClient;
  } catch (e) {
    return "error";
  }
};

export const getTenantAndToken = () => {
  const token = API.defaults.headers["Authorization"].split(" ")[1];
  const tenant = API.defaults.headers["X-Tenant-ID"];
  return [token, tenant];
};

export const getChatKpiData = async (performanceId) => {
  try {
    const response = await API.get("/performance/chat-details/" + performanceId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const acceptTargetORAch = async (performanceId, isTarget) => {
  try {
    const response = await API.post("/performance/accept-target-or-ach", null, {
      params: { performanceId, isTarget },
    });
  } catch (e) {
    return "error";
  }
};

export const rejectTargetORAch = async (performanceId, isTarget) => {
  try {
    const response = await API.post("/performance/reject-target-or-ach", null, {
      params: { performanceId, isTarget },
    });
  } catch (e) {
    return "error";
  }
};

export const deactivateChat = async (id) => {
  try {
    const response = await API.post("/chat-rooms/deactivate/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getChatroom = async (id) => {
  try {
    const response = await API.get("/chat-rooms/getchat/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const uploadChatFile = async (chatRoomId, senderId, content, file) => {
  try {
    const formData = new FormData();
    formData.append("chatRoomId", chatRoomId);
    formData.append("senderId", senderId);
    if (content) {
      formData.append("content", content);
    }
    formData.append("file", file);

    const response = await API.post("/chat-messages/chat/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (e) {
    return "error";
  }
};

export const downloadChatMessageFile = async (messageId, fileName) => {
  try {
    const response = await API.get(`/chat-messages/chat/download/${messageId}`, {
      responseType: "blob", // Important for file downloads
    });

    // Trigger file download
    saveAs(response.data, fileName);
  } catch (e) {
    return "error";
  }
};

export const getChatExport = async (chatRoomId, fileName) => {
  try {
    const response = await API.get("/chat-messages/export/" + chatRoomId, {
      responseType: "blob",
    });

    // Trigger the file download
    saveAs(response.data, `${fileName}.xlsx`);
  } catch (e) {
    return "error";
  }
};

export const uploadPhoto = async (id, photo) => {
  try {
    await API.post("/employee/upload-image", { id, photo });
  } catch (e) {
    return "error";
  }
};

export const getAreaPerformance = async (year, month, areaId) => {
  try {
    const response = await API.get("/kpi_assignments/find-result-by-area", {
      params: { year, month, areaId },
    });

    return response.data;
  } catch (e) {
    console.error(e);
    return "error";
  }
};

export const getJobGroups = async () => {
  try {
    const response = await API.get("/job_group");
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const addCompetency = async (competency) => {
  try {
    const response = await API.post("/competencies", competency);
    return response.data;
  } catch (e) {
    // console.error(`Error adding initiative with ID ${id}:`, e);
    // console.error("Server responded with:", error.response.data); // ✅ Show API error
    return "error";
  }
};

export const getCompetencies = async () => {
  try {
    const response = await API.get("/competencies");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getCompetencyLevels = async () => {
  try {
    const response = await API.get("/competency-level");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteCompetencyById = async (id) => {
  try {
    const response = await API.delete(`/competencies/${id}`);
    return response.data;
  } catch (e) {
    const message = e.response?.data || "Unknown error occurred";
    throw new Error(message);
    // return "error";
  }
};

export const addJobGroup = async (jobGroup) => {
  try {
    const response = await API.post("/job_group", jobGroup);
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const updateCompetency = async (comp) => {
  try {
    const response = await API.put("/competencies/" + comp.competencyId, comp);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addCompetencyLevel = async (competencyLevel) => {
  try {
    const response = await API.post("/competency-level", competencyLevel);
    return response.data;
  } catch (e) {
    console.error(`Error adding competency level with ID ${id}:`, e);
    // console.error("Server responded with:", error.response.data); // ✅ Show API error
    return "error";
  }
};

export const deleteJobGroup = async (jobGroupId) => {
  try {
    const response = await API.delete("/job_group/" + jobGroupId);
  } catch (e) {
    return "error";
  }
};
export const updateCompetencyLevel = async (compLevel) => {
  try {
    const response = await API.put("/competency-level/" + compLevel.competencyLevelId, compLevel);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addCompetencyToJob = async (competencyToJobModels) => {
  try {
    const response = await API.post("/competency_to_job", competencyToJobModels);
    return response.data;
  } catch (e) {
    console.error("Error adding CompetencyToJob:", e);
    return "error";
  }
};

export const getCompetencyToJobs = async () => {
  try {
    const response = await API.get("/competency_to_job");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteCompetencyToJob = async (id) => {
  try {
    const response = await API.delete(`/competency_to_job/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const exportData = async (fileName) => {
  try {
    // Determine API endpoint based on filename
    const endpoint =
      fileName === "areas"
        ? "/area/export"
        : fileName === "branches"
          ? "/branch/export"
          : fileName === "jobs"
            ? "/job/export"
            : fileName === "departments"
              ? "/department/export"
              : fileName === "employees"
                ? "/employee/export"
                : fileName === "kpis"
                  ? "/kpi_names/export"
                  : fileName === "scorecards"
                    ? "/kpi_assignments/export"
                    : fileName === "performance"
                      ? "/performance/export"
                      : fileName === "Company Kpis"
                        ? "/kpi_assignments/export-company"
                        : fileName === "scorecardTemplate"
                          ? "/kpi_assignments/export-template"
                          : null;
    if (!endpoint) return;
    const response = await API.get(endpoint, {
      responseType: "blob",
    });

    // Trigger the file download with the provided filename
    saveAs(response.data, `${fileName}.xlsx`);
  } catch (e) {
    return "error";
  }
};

export const importData = async (fileName, data) => {
  try {
    const endpoint =
      fileName === "areas"
        ? "/area/import"
        : fileName === "branches"
          ? "/branch/import"
          : fileName === "jobs"
            ? "/job/import"
            : fileName === "departments"
              ? "/department/import"
              : fileName === "employees"
                ? "/employee/import"
                : fileName === "kpis"
                  ? "/kpi_names/import"
                  : fileName === "scorecards"
                    ? "/kpi_assignments/import"
                    : fileName === "jobDetails"
                      ? "/job-details/import"
                      : fileName === "competencies"
                        ? "/competencies/import"
                        : fileName === "competencyLevels"
                          ? "/competency-level/import"
                          : fileName === "competencyToJob"
                            ? "/competency_to_job/import"
                            : fileName === "competencyToTraining"
                              ? "/comp_to_training/import"
                              : fileName === "learningAndDevelopment"
                                ? "/learning-development/import"
                                : null;

    if (!endpoint) return;

    const response = await API.post(endpoint, data);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const endCompetencyLevel = async (id) => {
  try {
    const response = await API.put(`/competency-level/end/${id}`);
    // const response = await API.put("/competency-level/" + comp.competencyId, comp);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getJobGroupsByJobId = async (id) => {
  try {
    const response = await API.get("job_group/job/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getTrainings = async () => {
  try {
    const response = await API.get("/comp_to_training");
    return response.data;
  } catch (e) {
    return "error";
  }
};

// export const getJobGroups = async () => {
//   try {
//     const response = await API.get("/job_group");
//     return response.data;
//   } catch (e) {
//     return "error";
//   }
// };

export const addCompetencyToTraining = async (competencyToTrainingModels) => {
  try {
    const response = await API.post("/comp_to_training", competencyToTrainingModels);
    return response.data;
  } catch (e) {
    console.error("Error adding CompetencyToTraining:", e);
    return "error";
  }
};

export const deleteCompetencyToTraining = async (id) => {
  try {
    const response = await API.delete("/comp_to_training/" + id);
  } catch (e) {
    return "error";
  }
};

export const getEmployeesForDirectManager = async (manager_id) => {
  try {
    const response = await API.get("/kpi_assignments/get-emps-for-direct_manager", {
      params: { manager_id },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getJobDetails = async (jobId, language) => {
  try {
    const response =
      language === "ar"
        ? await API.get("/job-details-ar/by-job/" + jobId)
        : await API.get("/job-details/by-job/" + jobId);
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const getCompetenciesForJob = async (jobId) => {
  try {
    const response = await API.get(`/competency_to_job/competencies/job/${jobId}`);
    return response.data;
  } catch (e) {
    // console.error("Error fetching employee competencies:", e);
    return "error";
  }
};

// UPDATED ✅
export const getCompetencyLevelsForJobOnDate = async (jobId, month, year) => {
  try {
    const response = await API.get("/competency_to_job/competencies", {
      params: { jobId, month, year },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Function to add Competency to Employee
export const addCompetencyToEmployee = async (competencyToEmployeeModel) => {
  try {
    const response = await API.post("/competency_to_employee", competencyToEmployeeModel);
    return response.data;
  } catch (e) {
    console.error("Error adding CompetencyToEmployee:", e);
    return "error";
  }
};

export const getCompetencyLevelForEmpOnDate = async (employeeId, month, year) => {
  try {
    const response = await API.get("/competency_to_employee/competency-level", {
      params: { employeeId, month, year },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Function to get latest competency level for an employee and competency
export const getLatestCompetencyLevel = async (employeeId, compId, month, year) => {
  try {
    const response = await API.get("/competency_to_employee/competency-level", {
      params: {
        employeeId,
        compId,
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching latest competency level:", e);
    return "error";
  }
};

export const getLatestCompetencyTraining = async (employeeId, compId, month, year) => {
  try {
    const response = await API.get("/competency_to_employee/competency-training", {
      params: {
        employeeId,
        compId,
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching latest competency level:", e);
    return "error";
  }
};

export const updateCompetencyLevelForEmp = async (id, compLevelId) => {
  try {
    const response = await API.put(`/competency_to_employee/update-level/${id}`, null, {
      params: { compLevelId },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating competency level:", error);
    return "error";
  }
};

export const getCompToTrainingByCompId = async (compId) => {
  try {
    const response = await API.get(`/comp_to_training/by-comp-id/${compId}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching CompToTraining by compId:", e);
    return "error";
  }
};

export const getCompetencyDataForJobAndEmployee = async (jobId, employeeId, year, month) => {
  try {
    const response = await API.get(`/competency_to_employee/job/${jobId}/employee/${employeeId}`, {
      params: { year, month },
    });
    return response.data; // List of merged competency data
  } catch (e) {
    console.error("Error fetching competency data for job and employee:", e);
    return "error";
  }
};

export const updateCompetencyTrainingForEmp = async (id, trainingId) => {
  try {
    const response = await API.put(`/competency_to_employee/update-training/${id}`, null, {
      params: { trainingId },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating competency training:", error);
    return "error";
  }
};

// New function for fetching Competency to Job data with job group name
export const getCompetenciesToJobsWithGroupName = async () => {
  try {
    const response = await API.get("/competency_to_job/competencies-to-jobs/with-job-group");
    return response.data;
  } catch (e) {
    console.error("Error fetching competencies to jobs with job group name:", e);
    return "error";
  }
};

export const updateJobDetails = async (jobDetailId, jobDetail, language) => {
  try {
    const reponse =
      language === "ar"
        ? await API.put("/job-details-ar/" + jobDetailId, jobDetail)
        : await API.put("/job-details/" + jobDetailId, jobDetail);
  } catch (e) {
    return "error";
  }
};

export const exportJD = async (jobId, jobName, language) => {
  try {
    const response =
      language === 1
        ? await API.get("/job-details-ar/export/" + jobId, {
            responseType: "blob",
          })
        : await API.get("/job-details/export/" + jobId, {
            responseType: "blob",
          });

    // Trigger the file download
    saveAs(response.data, `${jobName}'s JD.docx`);
  } catch (e) {
    return "error";
  }
};

export const getdashboardData = async (month, year, employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/perspective-dashboard", {
      params: { employeeId, month, year },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const changePassword = async (employeeId, currentPassword, newPassword) => {
  try {
    const response = await API.post("/employee/update-password", {
      employeeId: employeeId,
      currentPassword: currentPassword,
      newPassword: newPassword,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addRiskMatrix = async (businessDrivenData, addKey = true) => {
  try {
    // const response = await API.post("/businessDriven", businessDrivenData);
    const response = await API.post(
      `/businessDriven?addKey=${addKey}`, // ✅ pass addKey in URL
      businessDrivenData // ✅ body is still your object
    );
    return response.data;
  } catch (e) {
    console.error("Error adding business driven data:", e);
    if (e.response) {
      console.error("HTTP Status:", e.response.status);
      const message = e.response?.data || "Unknown error occurred";
      throw new Error(message);
    } else {
      console.error("Request failed:", e.message);
    }
    return "error";
  }
};

export const getRiskMatrices = async () => {
  try {
    const response = await API.get("/businessDriven");
    return response.data;
  } catch (e) {
    console.error("Error getting business driven data:", e);
    if (e.response) {
      console.error("HTTP Status:", e.response.status);
    } else {
      console.error("Request failed:", e.message);
    }
    return "error";
  }
};

export const updateRiskMatrix = async (riskMatrix) => {
  try {
    const response = await API.put("/businessDriven/" + riskMatrix.businessDrivenId, riskMatrix);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteRiskMatrixById = async (id) => {
  try {
    const response = await API.delete(`/businessDriven/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getMyCompetencies = async (employeeId, month, year) => {
  try {
    const response = await API.get("/competency_to_employee/my_comp", {
      params: { employeeId, month, year },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPolicies = async () => {
  try {
    const response = await API.get("/policies/titles");
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const getActiveKpisByJobId = async (jobId) => {
  try {
    const response = await API.get(`/strategic-roadmap/active-kpis/${jobId}`);
    return response.data; // Returns a list of [id, kpiNameId, kpiName]
  } catch (e) {
    console.error("Error fetching active KPIs by jobId:", e);
    return "error";
  }
};

export const addKpiLink = async (kpiLink) => {
  try {
    const response = await API.post("/strategic-roadmap", kpiLink);
    return response.data;
  } catch (e) {
    console.error("Error adding KPI link:", e);
    if (e.response) {
      console.error("HTTP Status:", e.response.status);
      const message =
        typeof e.response.data === "string"
          ? e.response.data
          : JSON.stringify(e.response.data) || "Unknown error occurred";
      throw new Error(message);
    } else {
      console.error("Request failed:", e.message);
      throw new Error("Network or unknown error occurred");
    }
  }
};

export const getKpiLinksWithNames = async () => {
  try {
    const response = await API.get("/strategic-roadmap/kpi-links");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteKpiLinkById = async (id) => {
  try {
    const response = await API.delete(`/strategic-roadmap/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPolicyById = async (id) => {
  try {
    const response = await API.get("/policies/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const getActiveKpisForCeo = async (jobId, month, year) => {
  try {
    const response = await API.get(`/strategic-roadmap/active-kpis/${jobId}/${month}/${year}`);
    return response.data; // Returns a list of [id, kpiNameId, weight, target, kpiName, perspectiveName, performanceTarget, achievement, secondaryAchievement]
  } catch (e) {
    console.error("Error fetching active KPIs by jobId, month, and year:", e);
    return "error";
  }
};

export const addPolicy = async (policy) => {
  try {
    const response = await API.post("/policies", policy);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deletePolicy = async (id) => {
  try {
    const response = await API.delete("/policies/" + id);
  } catch (e) {
    return "error";
  }
};

export const exportPolicy = async (policyId, policyName, language) => {
  try {
    const response = await API.get("/policies/export", {
      responseType: "blob",
      params: { policyId, language },
    });

    saveAs(response.data, `${policyName}.docx`);
  } catch (e) {
    return "error";
  }
};

export const exportHighLevelTemplate = async () => {
  try {
    const response = await API.get("/businessDriven/export-high-level", {
      responseType: "blob",
    });

    // Trigger the file download
    saveAs(response.data, `High Level Template.xlsx`);
  } catch (e) {
    return "error";
  }
};

export const jobFTEs = async () => {
  try {
    const response = await API.get("/job/ftes");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const fetchCeo = async () => {
  try {
    const response = await API.get("/employee/ceo");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const fetchChildren = async (id) => {
  try {
    const response = await API.get("/employee/subbordinates", { params: { id } });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getFunctionalByKpiAssignment = async (id) => {
  try {
    const response = await API.get("/functional/by-kpi/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const assignAllFunctionals = async (kpiAssignmentId, employeeIds) => {
  try {
    const response = await API.post("/functional/assign-all", {
      kpiAssignmentId,
      employeeIds,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const removeFunctionalsByKpiId = async (kpiAssignmentId) => {
  try {
    const response = await API.delete("/functional/remove-kpi", {
      data: { kpiAssignmentId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getFunctionalAssessorsByKpi = async (kpiAssignmentId) => {
  try {
    const response = await API.get("/functional/by-kpi/" + kpiAssignmentId);
    return response.data; // should be a list of employee IDs
  } catch (e) {
    return [];
  }
};

export const getFunctionalsForEmployee = async (employeeId, month, year) => {
  try {
    const response = await API.get("/functional/by-employee/" + employeeId, {
      params: {
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getFunctionalsForEmployeeQuarterly = async (employeeId, quarter, year) => {
  try {
    const response = await API.get("/functional/by-employee-quarterly/" + employeeId, {
      params: {
        quarter,
        year,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addEvidence = async (performanceId, file, isSelfAchievement) => {
  try {
    const formData = new FormData();
    formData.append("performanceId", performanceId);
    formData.append("file", file);

    const response = await API.post(
      isSelfAchievement ? "/self-achievement-evidence" : "/assessor-evidence",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (e) {
    console.error("Error uploading evidence:", e);
    return "error";
  }
};

export const saveFunctionalPerformance = async (performance) => {
  try {
    const response = await API.post("/functional-performance/save", performance);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAssessmentPageKpis = async (assessor_id, month, year, primaryAssessor) => {
  try {
    const response = await API.get("/kpi_assignments/assessment", {
      params: { month, year, assessor_id, primaryAssessor },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const getTrainingIdAndNameByEmployee = async (employeeId, year, month) => {
  try {
    const response = await API.get(
      `/learning-development/employee/${employeeId}/training-id-name`,
      {
        params: {
          year,
          month,
        },
      }
    );
    return response.data; // Returns List<Object[]>: [trainingId, trainingName, startDate, endDate, expectedOutcomeLevel]
  } catch (e) {
    console.error("Error fetching training ID and name by employee:", e);
    return "error";
  }
};

export const getAllTrainings = async (year, month) => {
  try {
    const response = await API.get(`/learning-development/employee/trainings`);
    return response.data; // Returns List<Object[]>: [trainingId, trainingName, startDate, endDate, expectedOutcomeLevel]
  } catch (e) {
    console.error("Error fetching training ID and name by employee:", e);
    return "error";
  }
};

// Add Training Record (Learning and Development)
export const addTrainingRecord = async (record) => {
  try {
    const response = await API.post("/learning-development", record);
    return response.data;
  } catch (e) {
    console.error("Error adding training record:", e);
    if (e.response) {
      console.error("HTTP Status:", e.response.status);
      const message =
        typeof e.response.data === "string"
          ? e.response.data
          : JSON.stringify(e.response.data) || "Unknown error occurred";
      throw new Error(message);
    } else {
      throw new Error("Network or unknown error occurred");
    }
  }
};

export const updateTrainingRecord = async (id, trainingRecord) => {
  try {
    const response = await API.put(`/learning-development/${id}`, trainingRecord);
    return response.data;
  } catch (error) {
    console.error("Error updating training record:", error);
    return "error";
  }
};

export const deleteTrainingRecordById = async (id) => {
  try {
    const response = await API.delete(`/learning-development/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getCompetenciesForManager = async (managerId, year, month) => {
  try {
    const response = await API.get(`/competency_to_employee/by-manager/${managerId}`, {
      params: {
        year,
        month,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching competencies for manager:", e);
    return "error";
  }
};

export const downloadEvidence = async (performanceId, fileName, isSelfEvidence) => {
  try {
    const response = await API.get(
      isSelfEvidence ? `/self-achievement-evidence/download` : `/assessor-evidence/download`,
      {
        responseType: "blob", // Important for file downloads
        params: {
          performanceId,
        },
      }
    );

    // Trigger file download
    saveAs(response.data, fileName);
  } catch (e) {
    return "error";
  }
};

export const getAllCompToTraining = async () => {
  try {
    const response = await API.get("/comp_to_training");
    return response.data;
  } catch (e) {
    console.error("Error fetching all CompToTraining:", e);
    return "error";
  }
};

export const getNotifiations = async (userId) => {
  try {
    const response = await API.get("/notification/" + userId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const readNotification = async (notificationId) => {
  try {
    const response = await API.post("/notification/read/" + notificationId);
  } catch (e) {
    return "error";
  }
};

export const readAllNotifications = async (userId) => {
  try {
    const response = await API.post("/notification/read-all/" + userId);
  } catch (e) {
    return "error";
  }
};

export const deleteNotification = async (notificationId) => {
  try {
    await API.delete("/notification/" + notificationId);
  } catch (e) {
    return "error";
  }
};

export const getEmployeesBasicInfo = async () => {
  try {
    const response = await API.get("/employee/basic-info");
    return response.data;
  } catch (e) {
    console.error("Error fetching employee basic info:", e);
    return "error";
  }
};

export const addSuccessionPlanRecord = async (record) => {
  try {
    const response = await API.post("/succession-plan", record);
    return response.data;
  } catch (e) {
    console.error("Error adding training succcess plan:", e);
    if (e.response) {
      console.error("HTTP Status:", e.response.status);
      const message =
        typeof e.response.data === "string"
          ? e.response.data
          : JSON.stringify(e.response.data) || "Unknown error occurred";
      throw new Error(message);
    } else {
      throw new Error("Network or unknown error occurred");
    }
  }
};

// Get Succession Plans by jobId only
export const getSuccessionPlans = async ({ jobId }) => {
  try {
    const response = await API.get("/succession-plan/plans", {
      params: { jobId },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching succession plans:", e);
    return "error";
  }
};

export const updateSucessionPlanRecord = async (id, succRecord) => {
  try {
    const response = await API.put(`/succession-plan/${id}`, succRecord);
    return response.data;
  } catch (error) {
    console.error("Error updating training record:", error);
    return "error";
  }
};

export const deleteSuccessionPlanRecordById = async (id) => {
  try {
    const response = await API.delete(`/succession-plan/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Get Succession Plans by Employee UUID
export const getSuccessionPlansByEmployeeUuid = async (employeeUuid) => {
  try {
    const response = await API.get("/succession-plan/plans-by-employee", {
      params: { employeeUuid },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching succession plans by employee UUID:", e);
    return "error";
  }
};

export const getQuarterlyAssessmentKpis = async (assessor_id, quarter, year, primaryAssessor) => {
  try {
    const response = await API.get("/kpi_assignments/quarterly_assessment", {
      params: { quarter, year, assessor_id, primaryAssessor },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllManpowerRequirements = async () => {
  try {
    const response = await API.get("/manpower-requirements");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addManpowerRequirement = async (req) => {
  try {
    const response = await API.post("/manpower-requirements", req);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getIsTopManagement = async (id) => {
  try {
    const res = await API.get("/employee/top-management/" + id);
    return res.data;
  } catch (e) {
    return "error";
  }
};

export const getManpowerDashboardData = async (jobId) => {
  try {
    const res = await API.get("/manpower-requirements/dashboards/" + jobId);
    return res.data;
  } catch (e) {
    return "error";
  }
};

export const updateAllEmployees = async (emps) => {
  try {
    const res = await API.put("/employee/update-all", emps);
  } catch (e) {
    return "error";
  }
};
// Get Functional Assessments for a KPI Assignment
export const getFunctionalAssessments = async (
  month,
  year,
  employeeIdAssessed,
  kpiAssignmentId
) => {
  try {
    const response = await API.get("kpi_assignments/functional-assessment", {
      params: {
        month,
        year,
        employeeIdAssessed,
        kpiAssignmentId,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching functional assessments:", e);
    return "error";
  }
};

export const getFunctionalAssessmentsQuarterly = async (
  quarter,
  year,
  employeeIdAssessed,
  kpiAssignmentId
) => {
  try {
    const response = await API.get("kpi_assignments/functional-assessment-quarterly", {
      params: {
        quarter,
        year,
        employeeIdAssessed,
        kpiAssignmentId,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching functional assessments:", e);
    return "error";
  }
};

export const updateArea = async (area) => {
  try {
    const response = await API.put("/area/" + area.areaId, area);
    return response.data; // return the updated AreaModel from backend
  } catch (e) {
    console.error("Update area failed:", e);
    return null; // return null if error
  }
};

export const updateBranch = async (branch) => {
  try {
    const response = await API.put("/branch/" + branch.branchId, branch);
    return response.data; // return the updated AreaModel from backend
  } catch (e) {
    console.error("Update area failed:", e);
    return null; // return null if error
  }
};

export const getScenarios = async (employeeId) => {
  try {
    const response = await API.get("/scenarios/employee/" + employeeId);
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const updateManpowerRequirement = async (id, req) => {
  try {
    const response = await API.put(`/manpower-requirements/${id}`, req);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getScenarioJobs = async (scenarioId) => {
  try {
    const response = await API.get("/scenario-jobs/scenario/" + scenarioId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addScenario = async (scenario) => {
  try {
    const response = await API.post("/scenarios", scenario);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteScenario = async (scenarioId) => {
  try {
    const response = await API.delete("/scenarios/" + scenarioId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addScenarioJob = async (scenarioJob) => {
  try {
    const response = await API.post("/scenario-jobs", scenarioJob);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteScenarioJob = async (id) => {
  try {
    const response = await API.delete("/scenario-jobs/" + id);
  } catch (e) {
    return "error";
  }
};

// 🔹 Get all modules & access rights for an employee
export const getEmployeeModules = async (employeeId) => {
  try {
    const response = await API.get(`/module-access/${employeeId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// 🔹 Grant or update access for one module
export const grantAccess = async (employeeId, moduleName, accessLevel) => {
  try {
    const response = await API.post(
      `/module-access/${employeeId}/${moduleName}?accessLevel=${accessLevel}`
    );
    return response.data;
  } catch (e) {
    return "error";
  }
};

// 🔹 Revoke access for one module
export const revokeAccess = async (employeeId, moduleName) => {
  try {
    await API.delete(`/module-access/${employeeId}/${moduleName}`);
    return "success";
  } catch (e) {
    return "error";
  }
};

// 🔹 Revoke ALL module access for an employee
export const revokeAllAccess = async (employeeId) => {
  try {
    await API.delete(`/module-access/${employeeId}`);
    return "success";
  } catch (e) {
    return "error";
  }
};

export const grantAllAccess = async (employeeId, accessLevel = "edit") => {
  try {
    await API.post(`/module-access/${employeeId}/grant-all?accessLevel=${accessLevel}`);
    return "success";
  } catch (e) {
    return "error";
  }
};

export const deleteMultipleJobs = async (ids) => {
  try {
    const res = await API.delete("/job/bulk-delete", {
      data: ids, // send IDs in the body
    });
    return res.data;
  } catch (e) {
    return "error";
  }
};

export const getAllErrorLogs = async (tenant) => {
  try {
    const res = await API.get("/super-admin/logs/" + tenant);
    return res.data;
  } catch {
    return "error";
  }
};

export const addErrorLog = async (error) => {
  try {
    const res = await API.post("/error-logs", error);
  } catch {
    return "error";
  }
};

export const addRevenueGenerator = async (revGenerator) => {
  try {
    const response = await API.post("/revenue-generator-team", revGenerator);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllRevenueGenerators = async (ids) => {
  try {
    const response = await API.post("/revenue-generator-team/jobs/byIds", ids);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateRevenueGenerator = async (id, revGenerator) => {
  try {
    const response = await API.put(`/revenue-generator-team/${id}`, revGenerator);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteRevenueGenerator = async (id) => {
  try {
    const response = await API.delete(`/revenue-generator-team/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllRevenueGeneratorsSimulation = async (jobIds) => {
  try {
    const response = await API.post("/revenue-generator-simulation/jobs/byIds", jobIds);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addRevenueGeneratorSim = async (revGenerator) => {
  try {
    const response = await API.post("/revenue-generator-simulation", revGenerator);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateRevenueGeneratorSim = async (id, revGenerator) => {
  try {
    const response = await API.put(`/revenue-generator-simulation/${id}`, revGenerator);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteRevenueGeneratorSim = async (id) => {
  try {
    const response = await API.delete(`/revenue-generator-simulation/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getManpowerOverviewExport = async () => {
  try {
    const response = await API.get("/manpower-requirements/export", {
      responseType: "blob",
    });

    // Extract filename from headers if available
    const contentDisposition = response.headers["content-disposition"];
    let filename = "manpower_overview.xlsx"; // Default fallback
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+?)"/);
      if (match) {
        filename = match[1];
      }
    }

    // Trigger the file download
    saveAs(response.data, filename);
  } catch (e) {
    console.error("Error exporting manpower overview:", e);
    return "error";
  }
};

export const getScenarioOverviewExport = async (scenarioId, scenarioName) => {
  try {
    const response = await API.get("/scenario-jobs/export/" + scenarioId, {
      responseType: "blob",
    });

    // Extract filename from headers if available
    const contentDisposition = response.headers["content-disposition"];
    let filename = scenarioName + "'s_overview.xlsx"; // Default fallback
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+?)"/);
      if (match) {
        filename = match[1];
      }
    }

    // Trigger the file download
    saveAs(response.data, filename);
  } catch (e) {
    console.error("Error exporting manpower overview:", e);
    return "error";
  }
};

export const deleteRevenueGeneratorsByJobId = async (jobId) => {
  try {
    const response = await API.delete(`/revenue-generator-team/job/${jobId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteRevenueGeneratorsSimulationByJobId = async (jobId) => {
  try {
    const response = await API.delete(`/revenue-generator-simulation/job/${jobId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getManpowerEmployeesByJobId = async (jobId) => {
  try {
    const response = await API.get("/manpower-requirements-employees/job/" + jobId);
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const getAllRevenueGeneratorsSimulationDashboard = async () => {
  try {
    const response = await API.get("/revenue-generator-simulation");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getScenarioEmployeesByJobId = async (jobId) => {
  try {
    const response = await API.get("/scenario-employees/job/" + jobId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addManpowerRequirementsEmployee = async (manpowerEmployee) => {
  try {
    const response = await API.post("/manpower-requirements-employees", manpowerEmployee);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addScenarioEmployee = async (scenarioEmployee) => {
  try {
    const response = await API.post("/scenario-employees", scenarioEmployee);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteScenarioEmployee = async (id) => {
  try {
    const response = await API.delete("/scenario-employees/" + id);
  } catch (e) {
    return "error";
  }
};

export const getScoresForScenario = async (id) => {
  try {
    const response = await API.get("/scenario-employees/scores/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getScoresForOrg = async (jobId) => {
  try {
    const response = await API.get("/manpower-requirements-employees/scores/" + jobId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getActiveModules = async () => {
  try {
    const response = await API.get("/modules/active");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllJobRequisitions = async () => {
  try {
    const response = await API.get("/job-requisitions");
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Get job requisitions by employee UUID
export const getJobRequisitionsByEmployee = async (employeeId) => {
  try {
    const response = await API.get(`/job-requisitions/employee/${employeeId}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching requisitions by employee:", e);
    return "error";
  }
};

export const saveJobRequisitions = async (data) => {
  try {
    const response = await API.post("/job-requisitions", data);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteJobRequisitions = async (id) => {
  try {
    const response = await API.delete("/job-requisitions/" + id);
  } catch (e) {
    return "error";
  }
};

export const getJobForm = async (id) => {
  try {
    const response = await API.get("/job-forms/requisition/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getJobFormByPublicId = async (id) => {
  try {
    const response = await API.get("/job-forms/public/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getJobFormQuestions = async (id) => {
  try {
    const response = await API.get("/job-form-questions/form/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const saveJobForm = async (form) => {
  try {
    const response = await API.post("/job-forms", form);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const saveJobFormQuestions = async (questions) => {
  try {
    const response = await API.post("/job-form-questions", questions);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getApplicantDataByForm = async (formId) => {
  try {
    const response = await API.get("/job-applications/applicants/" + formId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const downloadApplicantCV = async (applicationId, applicantName) => {
  try {
    const response = await API.get(`/job-application-cvs/download/${applicationId}`, {
      responseType: "blob", // <--- STILL CRITICAL: Don't forget this!
    });

    // 1. Try to get filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = `applicant_${applicantName}.pdf`; // Default fallback

    if (contentDisposition) {
      // Regex to grab content inside filename="example.pdf"
      const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (fileNameMatch && fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }

    // 2. Use file-saver to save
    saveAs(response.data, fileName);

    return true;
  } catch (e) {
    console.error("Download error", e);
    return "error";
  }
};

export const updateApplicantStatus = async (id, status) => {
  try {
    const response = await API.put(`/job-applications/${id}`, null, {
      params: { status: status },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateCompanyInfoUsingAI = async () => {
  try {
    const response = await API.get("/company/update-with-ai");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const exportRequisitionDetails = async (requisitionId, requisitionJobName) => {
  try {
    const response = await API.get(`/job-requisitions/${requisitionId}/export-applicants`, {
      responseType: "blob", // <--- STILL CRITICAL: Don't forget this!
    });

    // 1. Try to get filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = `applicants_requisition_${requisitionJobName}.xlsx`; // Default fallback

    if (contentDisposition) {
      // Regex to grab content inside filename="example.pdf"
      const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (fileNameMatch && fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }

    // 2. Use file-saver to save
    saveAs(response.data, fileName);

    return true;
  } catch (e) {
    console.error("Download error", e);
    return "error";
  }
};

export const exportRequisitionCVs = async (requisitionId, requisitionJobName) => {
  try {
    const response = await API.get(`/job-requisitions/${requisitionId}/export-cvs`, {
      responseType: "blob", // <--- STILL CRITICAL: Don't forget this!
    });

    // 1. Try to get filename from headers
    const contentDisposition = response.headers["content-disposition"];
    let fileName = `applicants_requisition_CVs_${requisitionJobName}.zip`; // Default fallback

    if (contentDisposition) {
      // Regex to grab content inside filename="example.pdf"
      const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (fileNameMatch && fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }

    // 2. Use file-saver to save
    saveAs(response.data, fileName);

    return true;
  } catch (e) {
    console.error("Download error", e);
    return "error";
  }
};

export const manpowerPlanningAI = async (id, isCompany) => {
  try {
    const response = await API.get(
      isCompany ? "/manpower-requirements/ai" : "/scenario-jobs/ai/" + id
    );
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Fetch meetings by form ID
export const getMeetingsByForm = async (formId) => {
  try {
    const response = await API.get("/talent-meetings/by-form/" + formId);
    return response.data;
  } catch (e) {
    console.error("Error fetching meetings by form:", e);
    return "error";
  }
};

export const addMeeting = async (meeting) => {
  try {
    const response = await API.post("/talent-meetings", meeting);
    return response.data; // returns the created TalentMeetingsModel
  } catch (e) {
    console.error("Error adding meeting:", e);
    return "error";
  }
};

export const getDepartmentDetailsByJobId = async (jobId) => {
  try {
    const response = await API.get(`/job/department-details/${jobId}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching department details by jobId:", e);
    return "error";
  }
};

export const updateMeetingComment = async (id, comment) => {
  try {
    const response = await API.put(`/talent-meetings/${id}/comment`, comment, {
      headers: {
        "Content-Type": "text/plain", // Crucial: Tells backend this is just text, not JSON
      },
    });
    return response.data; // returns the updated TalentMeetingsModel
  } catch (e) {
    console.error("Error updating meeting comment:", e);
    return "error";
  }
};

export const getMeetingDataByInterviewer = async (interviewerId, mode = 1) => {
  try {
    const response = await API.get(`/talent-meetings/by-interviewer/${interviewerId}?mode=${mode}`);
    return response.data; // This will be a Map<Long, List<TalentMeetingDetailsDTO>> on backend
  } catch (e) {
    console.error("Error fetching meetings by interviewer:", e);
    return "error";
  }
};

export const deleteMeetingById = async (id) => {
  try {
    const response = await API.delete(`/talent-meetings/${id}`);
    return response.data; // returns "Meeting deleted successfully" on success
  } catch (e) {
    console.error("Error deleting meeting:", e);
    return "error";
  }
};

export const updateMeetingDateTime = async (id, newDateTime) => {
  try {
    // newDateTime should be an ISO string like "2025-12-16T14:30:00"
    const response = await API.put(`/talent-meetings/${id}/datetime`, newDateTime, {
      headers: {
        "Content-Type": "application/json", // <--- CHANGE 2: Backend expects JSON
      },
    });
    return response.data; // returns the updated TalentMeetingsModel
  } catch (e) {
    console.error("Error updating meeting date/time:", e);
    return "error";
  }
};

export const getAllMeetings = async () => {
  try {
    const response = await API.get("/talent-meetings");
    return response.data; // List<TalentMeetingsModel>
  } catch (e) {
    console.error("Error fetching all meetings:", e);
    return "error";
  }
};

export const resetPassword = async (employeeId) => {
  try {
    const response = await API.post("/employee/reset-password", { employeeId });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const exportJobDetailsExcel = async (lang) => {
  try {
    const response =
      lang === "ar"
        ? await API.get("/job-details-ar/export-excel", {
            responseType: "blob", // <--- STILL CRITICAL: Don't forget this!
          })
        : await API.get("/job-details/export-excel", {
            responseType: "blob", // <--- STILL CRITICAL: Don't forget this!
          });

    // 2. Use file-saver to save
    saveAs(response.data, "Job_Details.xlsx");

    return true;
  } catch (e) {
    return "error";
  }
};
export const exportCompetencies = async () => {
  try {
    const response = await API.get("/competencies/export", {
      responseType: "blob",
    });

    // The Java backend specifies 'competencies.xlsx' in the headers,
    // but we define it here for the saveAs function.
    saveAs(response.data, "competencies.xlsx");

    return true;
  } catch (e) {
    console.error("Export error", e);
    return "error";
  }
};

export const exportCompetencyLevels = async () => {
  try {
    const response = await API.get("/competency-level/export", {
      responseType: "blob",
    });

    // The Java backend specifies 'competencies.xlsx' in the headers,
    // but we define it here for the saveAs function.
    saveAs(response.data, "competency_levels.xlsx");

    return true;
  } catch (e) {
    console.error("Export error", e);
    return "error";
  }
};

export const exportCompetencyToJob = async () => {
  try {
    const response = await API.get("/competency_to_job/export", {
      responseType: "blob",
    });

    // The Java backend specifies 'competencies.xlsx' in the headers,
    // but we define it here for the saveAs function.
    saveAs(response.data, "competency_to_job.xlsx");

    return true;
  } catch (e) {
    console.error("Export error", e);
    return "error";
  }
};

export const exportCompetencyToTraining = async () => {
  try {
    const response = await API.get("/comp_to_training/export", {
      responseType: "blob",
    });

    // The Java backend specifies 'competencies.xlsx' in the headers,
    // but we define it here for the saveAs function.
    saveAs(response.data, "competency_to_training.xlsx");

    return true;
  } catch (e) {
    console.error("Export error", e);
    return "error";
  }
};

export const exportLearningAndDevelopment = async () => {
  try {
    const response = await API.get("/learning-development/export", {
      responseType: "blob",
    });

    // The Java backend specifies 'competencies.xlsx' in the headers,
    // but we define it here for the saveAs function.
    saveAs(response.data, "learning_and_development.xlsx");

    return true;
  } catch (e) {
    console.error("Export error", e);
    return "error";
  }
};

export const getCompetencyToTraining = async () => {
  try {
    const response = await API.get("/comp_to_training");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllLearningAndDevelopment = async () => {
  try {
    const response = await API.get("/learning-development");
    return response.data;
  } catch (e) {
    console.error("Error fetching all learning and development:", e);
    return "error";
  }
};

export const getManpowerOrgChartData = async () => {
  try {
    const response = await API.get("/manpower-requirements/org-chart");
    return response.data; // returns List<OrganizationChartDTO> from backend
  } catch (e) {
    console.error("Error fetching org chart data:", e);
    return "error";
  }
};

// Get specific requirement details for the job
export const getRequirementDetails = async (requirementId) => {
  try {
    const response = await API.get(`/manpower-requirements/details/${requirementId}`);
    return response.data; // returns Map<String, Object>
  } catch (e) {
    console.error(`Error fetching details for requirement ${requirementId}:`, e);
    return "error";
  }
};

// Fetch scenario org chart data from backend
export const getScenarioOrgChartData = async (scenarioId) => {
  try {
    const response = await API.get("/scenario-jobs/org-chart/scenario", {
      params: { scenarioId }, // sends ?scenarioId=18
    });
    return response.data; // returns List<OrganizationChartDTO>
  } catch (e) {
    console.error("Error fetching scenario org chart data:", e);
    return "error";
  }
};

export const getScenarioJobDetails = async (scenarioJobId) => {
  try {
    const response = await API.get(`/scenario-jobs/details/${scenarioJobId}`);
    return response.data; // returns { scenarioJob: ScenarioJobDTO, revenueRecords?: RevGeneratorModel[] }
  } catch (e) {
    console.error(`Error fetching details for scenario job ${scenarioJobId}:`, e);
    return "error";
  }
};

export const deleteEvidence = async (performanceId) => {
  try {
    const response = await API.delete(`/assessor-evidence/remove?performanceId=${performanceId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addCommentToEvidence = async (performanceId, comment) => {
  try {
    const response = await API.post(`/assessor-evidence/comment`, null, {
      params: { performanceId, comment },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};
// Frontend API call for update
export const updateScenarioJobDetails = async (id, payload) => {
  try {
    const response = await API.put(`/scenario-jobs/update/${id}`, payload);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const cloneScenario = async (scenarioId, scenarioRequest) => {
  try {
    const response = await API.post(`/scenarios/${scenarioId}/clone`, scenarioRequest);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Fetch all page data for job requisitions (jobs, branches, departments, jobToBranches, jobRequisitions, employees)
// export const getJobRequisitionsPageData = async () => {
//   try {
//     const response = await API.get("/job-requisitions/page-data");
//     return response.data; // returns JobRequisitionsPageDTO
//   } catch (e) {
//     console.error("Error fetching job requisitions page data:", e);
//     return "error";
//   }
// };

// Fetch all page data for job requisitions (jobs, branches, departments, jobToBranches, jobRequisitions, employees)
export const getJobRequisitionsPageData = async (employeeId) => {
  try {
    if (!employeeId) {
      throw new Error("employeeId is required");
    }

    const response = await API.get(`/job-requisitions/page-data`, {
      params: { employeeId }, // send as query parameter
    });

    return response.data; // returns JobRequisitionsPageDTO
  } catch (e) {
    console.error("Error fetching job requisitions page data:", e);
    return "error";
  }
};

export const cloneScenarioFromCompany = async (scenarioRequest) => {
  try {
    const response = await API.post(`/scenarios/clone-from-company`, scenarioRequest);
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const getApplicantsPageData = async (formId) => {
  try {
    const response = await API.get(`/job-applications/page/applicants`, {
      params: { formId },
    });

    return response.data;
  } catch (e) {
    console.error("Failed to fetch applicants page data", e);
    throw e;
  }
};

// Fetch job form page data (form + questions) by requisitionId
export const getJobFormPageData = async (requisitionId) => {
  try {
    if (!requisitionId) {
      throw new Error("requisitionId is required");
    }

    const response = await API.get(`/job-forms/page/${requisitionId}`);
    return response.data; // returns JobFormPageDTO
  } catch (e) {
    console.error("Failed to fetch job form page data", e);
    return "error";
  }
};

export const cloneJobforScenario = async (
  targetScenarioId,
  sourceJobId,
  sourceType,
  targetManagerId,
  numberOfEmployees,
  salary
) => {
  try {
    const response = await API.post(`/scenario-jobs/clone-job`, null, {
      params: {
        targetScenarioId,
        sourceJobId,
        sourceType,
        targetManagerId,
        numberOfEmployees,
        salary,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Fetch dashboard page data for corporate KPI view
export const getDashboardPageData = async (
  year,
  month,
  employeeId,
  companyId,
  isQuarterly = false
) => {
  try {
    const response = await API.get("/kpi_assignments/page-data", {
      params: { year, month, employeeId, companyId, isQuarterly },
    });
    return response.data; // returns CorporatePageDashboardDTO
  } catch (e) {
    console.error("Error fetching dashboard page data:", e);
    return "error";
  }
};

export const getUserDashboardPageData = async (departmentId, employeeId) => {
  try {
    const response = await API.get("/kpi_assignments/user-dashboard", {
      params: { departmentId, employeeId },
    });
    return response.data; // returns CorporatePageDashboardDTO
  } catch (e) {
    console.error("Error fetching dashboard page data:", e);
    return "error";
  }
};

export const getUserDashboardPageOtherData = async (
  year,
  month,
  departmentId,
  employeeId,
  isAdmin
) => {
  try {
    const response = await API.get("/kpi_assignments/other-user", {
      params: {
        year,
        month,
        departmentId,
        employeeId,
        isAdmin,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching dashboard page data:", e);
    return "error";
  }
};

export const getAnalysisPageData = async (rootJobId) => {
  try {
    const response = await API.get("manpower-requirements/analysis-page", {
      params: {
        rootJobId,
      },
    });
    return response.data; // returns AnalysisDTO
  } catch (e) {
    console.error("Error fetching analysis page data:", e);
    return "error";
  }
};

export const updateGaharJob = async (gaharJobData) => {
  try {
    const response = await API.post("/scenario-jobs/update-gahar-job", gaharJobData);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateGaharRatios = async (jobId, ratio, isDay) => {
  try {
    const response = await API.post("/scenario-jobs/update-gahar-ratio", { jobId, ratio, isDay });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getCompMyTeamPageData = async (employeeId, month, year) => {
  try {
    const response = await API.get("/competency_to_job/team/competencies", {
      params: {
        employeeId,
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching team data:", e);
    return "error";
  }
};

export const addJobGroupsBulk = async (jobId, jobGroups) => {
  try {
    const response = await API.post("/job_group/bulk", {
      jobId: jobId,
      jobGroups: jobGroups,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const downloadAllEvidencesZip = async (performanceData, employeeName, month, year) => {
  if (!performanceData || performanceData.length === 0) return;

  try {
    const response = await API.post(
      `/self-achievement-evidence/export-evidences`,
      performanceData, // now sending array of {performanceId, kpiName}
      {
        responseType: "blob", // for file download
        params: {
          // query params for @RequestParam
          employeeName,
          month,
          year,
        },
        validateStatus: (status) => status === 200 || status === 204,
      }
    );
    if (response.status === 204) {
      console.warn("No evidences to download");
      return;
    }

    const fileName = `Evidence_${employeeName}_${month}_${year}.zip`;
    saveAs(response.data, fileName);
  } catch (e) {
    console.error("Error downloading evidences ZIP:", e);
    return "error";
  }
};

export const getAllCompaniesForSuperUser = async () => {
  try {
    const response = await API.get("/super-admin/companies");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const impersonateCEOAsSuperAdmin = async (tenantId) => {
  try {
    const response = await API.post("/super-admin/impersonate/" + tenantId);
    return response.data;
  } catch (e) {
    console.error(e);
    return null;
  }
};
export const getAllCompanyModulesAsSuperAdmin = async (tenantId) => {
  try {
    const response = await API.get("/super-admin/modules/" + tenantId);
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const updateCompanyModuleAsSuperAdmin = async (tenantId, module, active) => {
  try {
    const response = await API.post("/super-admin/modules/update", {
      tenantId,
      moduleKey: module,
      turnedOn: active,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};
export const updateMultipleCompanyModulesAsSuperAdmin = async (tenantId, modules) => {
  try {
    const response = await API.post("/super-admin/modules/update-bulk", { tenantId, modules });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const createTicket = async (ticketData) => {
  try {
    const response = await API.post("/tickets/create", ticketData);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getTicketsForEmployee = async () => {
  try {
    const response = await API.get("/tickets/my-tickets");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllTicketsForSuperAdmin = async (tenantId) => {
  try {
    const response = await API.get("/super-admin/tickets/" + tenantId);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateTicketStatus = async (tenantId, ticketId, adminSeverity, status) => {
  try {
    const response = await API.post(`/super-admin/tickets/update`, {
      tenantId,
      ticketId,
      adminSeverity,
      status,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const assignAllEvidenceProviders = async (kpiAssignmentId, employeeIds) => {
  try {
    const response = await API.post("/evidence-provider/assign-all", {
      kpiAssignmentId,
      employeeIds,
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEvidenceProvidersByKpiAssignment = async (id) => {
  try {
    const response = await API.get("/evidence-provider/by-kpi/" + id);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const removeEvidenceProvidersByKpiId = async (kpiAssignmentId) => {
  try {
    const response = await API.delete("/evidence-provider/remove-kpi", {
      data: { kpiAssignmentId },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllEvidenceProviders = async () => {
  try {
    const response = await API.get("/evidence-provider/all");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getAllEvidenceProvidersWithUid = async () => {
  try {
    const response = await API.get("/evidence-provider/all-uid");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getEvidenceDataForProvider = async (employeeId, month, year) => {
  try {
    const response = await API.get("/evidence-provider/by-employee/" + employeeId, {
      params: {
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const saveEvidenceData = async ({ evidenceId, employeeId, month, year, comment, file }) => {
  try {
    const formData = new FormData();
    formData.append("evidenceId", evidenceId);
    formData.append("employeeId", employeeId);
    formData.append("month", month);
    formData.append("year", year);
    formData.append("comment", comment || ""); // optional comment
    if (file) {
      formData.append("file", file); // file object from input
    }

    const response = await API.post("/evidence-data/save", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data; // returns the saved EvidenceDataDTO
  } catch (e) {
    console.error("Error saving evidence data:", e);
    return "error";
  }
};

export const downloadProviderEvidenceData = async (
  evidenceId,
  employeeId,
  month,
  year,
  fileName
) => {
  try {
    const response = await API.get("/evidence-data/download", {
      responseType: "blob", // REQUIRED for file
      params: {
        evidenceId,
        employeeId,
        month,
        year,
      },
    });

    saveAs(response.data, fileName);
  } catch (e) {
    console.error("Error downloading evidence:", e);
    return "error";
  }
};

// Frontend API call to update only the comment of an evidence record
export const updateEvidenceComment = async (evidenceId, employeeId, month, year, comment) => {
  try {
    const response = await API.put("/evidence-data/comment", null, {
      params: {
        evidenceId,
        employeeId,
        month,
        year,
        comment: comment ?? "",
      },
    });

    return response.data;
  } catch (e) {
    console.error("Error updating evidence comment:", e);
    return "error";
  }
};

export const getEvidenceDataForProviderQuarterly = async (employeeId, quarter, year) => {
  try {
    const response = await API.get("/evidence-provider/by-employee-quarterly/" + employeeId, {
      params: {
        quarter,
        year,
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Frontend API call to delete an evidence record
export const deleteProviderEvidence = async (evidenceId, employeeId, month, year) => {
  try {
    const response = await API.delete("/evidence-data/delete", {
      params: {
        evidenceId,
        employeeId,
        month,
        year,
      },
    });

    return response.data; // "Evidence deleted successfully"
  } catch (e) {
    console.error("Error deleting evidence:", e);
    return "error";
  }
};

export const updateEmployeeDetail = async (employeeDetailId, dto) => {
  try {
    const response = await API.put(`/employee-details/${employeeDetailId}`, dto);
    return response.data;
  } catch (e) {
    console.error("Error updating employee detail:", e);
    if (e.response) {
      console.error("HTTP Status:", e.response.status);
      throw new Error(e.response?.data || "Unknown error occurred");
    }
    return "error";
  }
};

export const getEmployeeDetailsByUuid = async (employeeUuid) => {
  try {
    const response = await API.get(`/employee-details/by-employee-uuid/${employeeUuid}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching employee by UUID:", e);
    if (e.response) {
      console.error("HTTP Status:", e.response.status);
      throw new Error(e.response?.data || "Unknown error occurred");
    }
    return "error";
  }
};

export const saveEmployeeCV = async ({ employeeId, file }) => {
  try {
    const formData = new FormData();
    formData.append("employeeId", employeeId); // required
    if (file) {
      formData.append("file", file); // file object from input
    }

    const response = await API.post("/employee-cv/save", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data; // returns the saved EmployeeCVDTO
  } catch (e) {
    console.error("Error saving employee CV:", e);
    return "error";
  }
};

export const downloadEmployeeCV = async (employeeId, employeeName) => {
  if (!employeeId || !employeeName) return { success: false };

  try {
    // ✅ Send both employeeId and employeeName to backend
    const response = await API.get("/employee-cv/download", {
      params: { employeeId, employeeName }, // pass employeeName
      responseType: "blob", // important for file download
    });

    // Construct filename locally (optional, same as backend)
    const sanitizedName = employeeName.replace(/\s+/g, "_");
    const fileName = `${sanitizedName}_CV.pdf`;

    // Save file
    saveAs(response.data, fileName);

    return { success: true, fileName };
  } catch (err) {
    console.error("Error downloading CV:", err);
    return "error";
  }
};

export const employeeCVExists = async (employeeId) => {
  if (!employeeId) return false;

  try {
    const response = await API.get("/employee-cv/exists", {
      params: { employeeId },
    });

    // The backend returns { exists: true/false }
    return response.data.exists === true;
  } catch (err) {
    console.error("Error checking if employee CV exists:", err);
    return "error";
  }
};

export const getCompetencyKpiLinks = async (competencyId) => {
  try {
    const response = await API.get(`/competency-kpis/competency/${competencyId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const linkCompetencyToKpis = async (payload) => {
  try {
    const response = await API.post(`/competency-kpis/link`, payload);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const ExportOrgChartPPTX = async () => {
  try {
    const response = await API.get("/job/org-chart/export", {
      responseType: "blob",
    });

    saveAs(response.data, "org-chart.pptx");
  } catch (e) {
    return "error";
  }
};

export const getJobSuccessionPlanData = async () => {
  try {
    const response = await API.get("/job/succession-plan-data");
    return response.data;
  } catch (e) {
    console.error("Error fetching succession plan data", e);
    return "error";
  }
};

export const updateJobCriticality = async (id, criticality) => {
  try {
    const response = await API.put(`/job/${id}/criticality`, criticality, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addEmployeeSuccessor = async (successor) => {
  try {
    const response = await API.post("/successors", successor);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getSuccessorsByJobId = async (jobId) => {
  try {
    const response = await API.get(`/successors/by-job/${jobId}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching successors by jobId", e);
    return "error";
  }
};

export const getStepsBySuccessorId = async (successorId) => {
  try {
    const response = await API.get(`/succession-plan-steps/successor/${successorId}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching steps by successorId", e);
    return "error";
  }
};

export const addSuccessionPlanStep = async (plan) => {
  try {
    const response = await API.post("/succession-plan-steps", plan);
    return response.data;
  } catch (e) {
    console.error("Error adding succession plan step", e);
    return "error";
  }
};

export const deleteSuccessionPlanStep = async (id) => {
  try {
    const response = await API.delete(`/succession-plan-steps/${id}`);
    return response.data;
  } catch (e) {
    console.error("Error deleting succession plan step", e);
    return "error";
  }
};

export const updateSuccessionPlanStep = async (id, updatedPlan) => {
  try {
    const response = await API.put(`/succession-plan-steps/${id}`, updatedPlan, {
      headers: { "Content-Type": "application/json" },
    });

    return response.data;
  } catch (e) {
    console.error("Error updating succession plan step", e);
    return "error";
  }
};

export const addSuccessionPlanStepTraining = async (stepTraining) => {
  try {
    const response = await API.post("/succession-plan-step-trainings", stepTraining);

    return response.data;
  } catch (e) {
    console.error("Error adding succession plan step training", e);
    return "error";
  }
};

export const getSuccessorsWithStepsByJobId = async (jobId) => {
  try {
    const response = await API.get(`/successors/by-job-with-steps/${jobId}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching successors by jobId", e);
    return "error";
  }
};

export const addLinkHRToRequisition = async (requisitionId, employeeId) => {
  try {
    const response = await API.post(`/requisition-employee/assign`, null, {
      params: { employeeId, requisitionId },
    });
    return response.data;
  } catch (e) {
    console.error("Error adding HR link to requisition", e);
    return "error";
  }
};

export const HRToRequisitionLinkExists = async (requisitionId, employeeId) => {
  try {
    const response = await API.get(`/requisition-employee/exists`, {
      params: { employeeId, requisitionId },
    });
    return response.data;
  } catch (e) {
    console.error("Error checking HR link existence", e);
    return "error";
  }
};
export const deleteStepTraining = async (id) => {
  try {
    const response = await API.delete(`/succession-plan-step-trainings/${id}`);
    return response.data;
  } catch (e) {
    console.error("Error deleting step training", e);
    return "error";
  }
};

export const deleteSuccessor = async (id) => {
  try {
    const response = await API.delete(`/successors/${id}`);
    return response.data;
  } catch (e) {
    console.error("Error deleting successor", e);
    return "error";
  }
};

export const createJobCompetencyPoint = async (point) => {
  try {
    const response = await API.post("/job-competency-points", point);

    return response.data;
  } catch (e) {
    console.error("Error creating competency point", e);
    return "error";
  }
};

export const updateJobCompetencyPoint = async (id, point) => {
  try {
    const response = await API.put(`/job-competency-points/${id}`, point);

    return response.data;
  } catch (e) {
    console.error("Error updating competency point", e);
    return "error";
  }
};

export const deleteJobCompetencyPoint = async (id) => {
  try {
    const response = await API.delete(`/job-competency-points/${id}`);

    return response.data;
  } catch (e) {
    console.error("Error deleting competency point", e);
    return "error";
  }
};

export const getJobCompetencyPoints = async (compToJobId) => {
  try {
    const response = await API.get(`/job-competency-points/comp-to-job/${compToJobId}`);

    return response.data;
  } catch (e) {
    console.error("Error getting competency points", e);
    return "error";
  }
};

export const getAllJobCompetencyPoints = async () => {
  try {
    const response = await API.get(`/job-competency-points`);
    return response.data;
  } catch (e) {
    console.error("Error getting all competency points", e);
    return "error";
  }
};

// Employee Competency Points API functions

export const getEmployeeCompetencyPoints = async (employeeId, compToJobId) => {
  try {
    const response = await API.get(
      `/employee-competency-points/details/${employeeId}/${compToJobId}`
    );
    return response.data;
  } catch (e) {
    console.error("Error getting employee competency points", e);
    return "error";
  }
};

export const getEmployeePointsSummary = async (employeeId, compToJobId) => {
  try {
    const response = await API.get(
      `/employee-competency-points/summary/${employeeId}/${compToJobId}`
    );
    return response.data;
  } catch (e) {
    console.error("Error getting employee points summary", e);
    return "error";
  }
};

export const syncEmployeePoints = async (employeeId, compToJobId) => {
  try {
    const response = await API.post(
      `/employee-competency-points/sync/${employeeId}/${compToJobId}`
    );
    return response.data;
  } catch (e) {
    console.error("Error syncing employee points", e);
    return "error";
  }
};

export const addPointsToEmployee = async (employeeId, pointIds) => {
  try {
    const response = await API.post(`/employee-competency-points/add/${employeeId}`, pointIds);
    return response.data;
  } catch (e) {
    console.error("Error adding points to employee", e);
    return "error";
  }
};

export const removePointsFromEmployee = async (employeeId, pointIds) => {
  try {
    const response = await API.delete(`/employee-competency-points/remove/${employeeId}`, {
      data: pointIds,
    });
    return response.data;
  } catch (e) {
    console.error("Error removing points from employee", e);
    return "error";
  }
};
export const saveCompToEmployeeEvidenceData = async ({ compToEmpId, comment, file }) => {
  try {
    const formData = new FormData();

    formData.append("compToEmpId", compToEmpId);
    formData.append("comment", comment || "");

    if (file) {
      formData.append("file", file);
    }

    const response = await API.post("/competency-to-employee-evidence/save", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data; // returns CompToEmployeeEvidenceDTO
  } catch (e) {
    console.error("Error saving competency evidence data:", e);
    return "error";
  }
};

export const recalculateEmployeeLevel = async (employeeId, compToJobId) => {
  try {
    const response = await API.post(
      `/employee-competency-points/recalculate/${employeeId}/${compToJobId}`
    );
    return response.data;
  } catch (e) {
    console.error("Error recalculating employee level", e);
    return "error";
  }
};
export const downloadCompToEmployeeEvidenceData = async (compToEmpId, fileName) => {
  try {
    const response = await API.get("/competency-to-employee-evidence/download", {
      responseType: "blob", // REQUIRED for file download
      params: {
        compToEmpId,
      },
    });

    saveAs(response.data, fileName);
  } catch (e) {
    console.error("Error downloading competency evidence:", e);
    return "error";
  }
};

export const getAllEmployeeCompetencyPoints = async (employeeId) => {
  try {
    const response = await API.get(`/employee-competency-points/employee/${employeeId}`);
    return response.data;
  } catch (e) {
    console.error("Error getting all employee competency points", e);
    return "error";
  }
};
export const updateCompToEmployeeEvidenceComment = async (compToEmpId, comment) => {
  try {
    const response = await API.put("/competency-to-employee-evidence/comment", null, {
      params: {
        compToEmpId,
        comment: comment ?? "",
      },
    });

    return response.data;
  } catch (e) {
    console.error("Error updating competency evidence comment:", e);
    return "error";
  }
};

export const deleteAllEmployeePointsForMapping = async (employeeId, compToJobId) => {
  try {
    const response = await API.delete(
      `/employee-competency-points/employee/${employeeId}/mapping/${compToJobId}`
    );
    return response.data;
  } catch (e) {
    console.error("Error deleting employee points for mapping", e);
    return "error";
  }
};
export const deleteCompToEmployeeEvidence = async (compToEmpId) => {
  try {
    const response = await API.delete("/competency-to-employee-evidence/delete", {
      params: {
        compToEmpId,
      },
    });

    return response.data;
  } catch (e) {
    console.error("Error deleting competency evidence:", e);
    return "error";
  }
};

export const addAdminPerformanceComment = async (commentData) => {
  try {
    const response = await API.post("/performance-comments", commentData);
    return response.data;
  } catch (e) {
    console.error("Error saving performance comment:", e);
    return "error";
  }
};

export const getPerformanceComments = async (uId, currentUserUid, month, year) => {
  try {
    const response = await API.get("/performance/comments", {
      params: {
        uId, // The employee being viewed
        currentUserUid, // The logged-in user
        month,
        year,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching performance comments:", e);
    return "error";
  }
};

export const resetPerformanceAchievement = async (id) => {
  try {
    const response = await API.put(`/performance/reset-achievement/${id}`);

    return response.data;
  } catch (e) {
    console.error("Error resetting performance achievement:", e);
    return "error";
  }
};

export const getJobCompetencyMatrix = async (jobId, month, year, currentUserUid) => {
  try {
    const response = await API.get(`/competency_to_employee/admin/${jobId}`, {
      params: {
        month,
        year,
        currentUserUid,
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error fetching job competency matrix:", e);
    return "error";
  }
};

export const addAdminCompetencyEvaluationComment = async (commentData) => {
  try {
    const response = await API.post("/competency-assessment-comments", commentData);
    return response.data;
  } catch (e) {
    console.error("Error saving performance comment:", e);
    return "error";
  }
};

export const resetCompAssessment = async (compToEmpId) => {
  try {
    // Assuming the controller is mapped under /competency_to_employee
    const response = await API.delete(
      `/competency_to_employee/reset-comp-assessment/${compToEmpId}`
    );

    return response.data;
  } catch (e) {
    console.error("Error resetting competency assessment:", e);
    return "error";
  }
};

// Add this to your API file
export const getAllCompetencyKpiLinks = async () => {
  try {
    const response = await API.get("/competency-kpis");
    return response.data;
  } catch (e) {
    console.error("Error fetching all competency KPI links:", e);
    return "error";
  }
};

export const getMyTrainingsPageData = async (employeeId, month, year) => {
  try {
    const response = await API.get(`/competency_to_employee/my-trainings-page/${employeeId}`, {
      params: { month, year },
    });

    return response.data;
  } catch (e) {
    console.error("Error fetching trainings page data:", e);
    return "error";
  }
};

//Saves a single day's workload to the database
export const saveEmployeeWorkload = async (payload) => {
  try {
    const response = await API.post(`/employee-workload`, payload);
    return response.data;
  } catch (e) {
    console.error("Error saving employee workload:", e);
    return "error";
  }
};

export const saveBatchWorkload = async (payloadArray) => {
  try {
    const response = await API.post(`/employee-workload/batch`, payloadArray);
    return response.data;
  } catch (e) {
    console.error("Error saving batch employee workloads:", e);
    return "error";
  }
};

export const updateJobContractualHours = async (jobId, hours) => {
  try {
    const response = await API.put(`/jobs/${jobId}/contractual-hours`, hours, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (e) {
    console.error("Error updating job contractual hours:", e);
    return "error";
  }
};

export const updateBatchWorkload = async (payloadArray) => {
  try {
    // We use .put for updates
    const response = await API.put(`/employee-workload/batch`, payloadArray);
    return response.data;
  } catch (e) {
    console.error("Error updating batch employee workloads:", e);
    return "error";
  }
};

export const getWorkloadsByJobId = async (jobId) => {
  try {
    const response = await API.get(`/employee-workload/job/${jobId}`);
    return response.data;
  } catch (e) {
    console.error(`Error fetching employee workloads for job ID ${jobId}:`, e);
    return "error";
  }
};

export const resetKpiAssignments = async (jobId, branchId) => {
  try {
    const response = await API.post(`/kpi_assignments/${jobId}/${branchId}/reset-kpi-assignments`);
    return response;
  } catch (e) {
    console.error(`Error resetting KPI assignments for job ID ${jobId}:`, e);
    return "error";
  }
};

// services/api.js - Add these to your existing api.js file

export const getSalaryComponents = async () => {
  try {
    const response = await API.get("/salary-component");
    return response.data;
  } catch (e) {
    console.error("Error fetching salary components:", e);
    return "error";
  }
};

export const getActiveSalaryComponents = async () => {
  try {
    const response = await API.get("/salary-component/active");
    return response.data;
  } catch (e) {
    console.error("Error fetching active salary components:", e);
    return "error";
  }
};

export const getSalaryComponentById = async (id) => {
  try {
    const response = await API.get(`/salary-component/${id}`);
    return response.data;
  } catch (e) {
    console.error(`Error fetching salary component with ID ${id}:`, e);
    return "error";
  }
};

export const addSalaryComponent = async (payload) => {
  try {
    const response = await API.post("/salary-component", payload);
    return response.data;
  } catch (e) {
    console.error("Error adding salary component:", e);
    return "error";
  }
};

export const updateSalaryComponent = async (id, payload) => {
  try {
    const response = await API.put(`/salary-component/${id}`, payload);
    return response.data;
  } catch (e) {
    console.error(`Error updating salary component with ID ${id}:`, e);
    return "error";
  }
};

export const deleteSalaryComponent = async (id) => {
  try {
    const response = await API.delete(`/salary-component/${id}`);
    return response.data;
  } catch (e) {
    console.error(`Error deleting salary component with ID ${id}:`, e);
    return "error";
  }
};

//PAYROLL STRUCTURE DATA
export const getPayrollStructures = async () => {
  try {
    const response = await API.get("/payroll-structure");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPayrollStructuresByJobId = async (jobId) => {
  try {
    const response = await API.get(`/payroll-structure/by-job/${jobId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addPayrollStructure = async (structure) => {
  try {
    const response = await API.post("/payroll-structure", structure);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updatePayrollStructure = async (id, structure) => {
  try {
    const response = await API.put(`/payroll-structure/${id}`, structure);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deletePayrollStructure = async (id) => {
  try {
    const response = await API.delete(`/payroll-structure/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};
//END PAYROLL STRUCTURE DATA

//PAYROLL STRUCTURE COMPONENT DATA
export const getComponentsForStructure = async (structureId) => {
  try {
    const response = await API.get(`/payroll-structure-component/structure/${structureId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addStructureComponent = async (structureComponent) => {
  try {
    const response = await API.post("/payroll-structure-component", structureComponent);
    return response.data;
  } catch (e) {
    // Backend validates amount/percentage against the linked component's
    // calculation_type and rejects duplicates - surface the real message
    return e.response?.data?.message || e.response?.data || "error";
  }
};

export const deleteStructureComponent = async (id) => {
  try {
    const response = await API.delete(`/payroll-structure-component/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};
//END PAYROLL STRUCTURE COMPONENT DATA

//PAYROLL INPUT TEMPLATE DATA
export const getInputTemplates = async () => {
  try {
    const response = await API.get("/payroll-input-template");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addInputTemplate = async (template) => {
  try {
    const response = await API.post("/payroll-input-template", template);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updateInputTemplate = async (id, template) => {
  try {
    const response = await API.put(`/payroll-input-template/${id}`, template);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deleteInputTemplate = async (id) => {
  try {
    const response = await API.delete(`/payroll-input-template/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};
//END PAYROLL INPUT TEMPLATE DATA

//PAYROLL INPUT TEMPLATE DETAIL DATA
export const getTemplateDetails = async (templateId) => {
  try {
    const response = await API.get(`/payroll-input-template-detail/template/${templateId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addTemplateDetail = async (detail) => {
  try {
    const response = await API.post("/payroll-input-template-detail", detail);
    return response.data;
  } catch (e) {
    // Backend rejects duplicates (same component attached twice) with a clear message
    return e.response?.data?.message || e.response?.data || "error";
  }
};

export const deleteTemplateDetail = async (id) => {
  try {
    const response = await API.delete(`/payroll-input-template-detail/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};
//END PAYROLL INPUT TEMPLATE DETAIL DATA

//EMPLOYEE COMPENSATION DATA

export const getActiveCompensation = async (employeeId) => {
  try {
    const response = await API.get(`/employee-compensation/employee/${employeeId}/active`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Real raise/promotion/hire - auto-supersedes the employee's current active row
export const assignCompensation = async (compensation) => {
  try {
    const response = await API.post("/employee-compensation", compensation);
    return response.data;
  } catch (e) {
    return e.response?.data?.message || "error";
  }
};

// Fixes a data-entry mistake WITHOUT triggering supersede logic - never use for an actual raise
export const correctCompensation = async (id, compensation) => {
  try {
    const response = await API.put(`/employee-compensation/${id}`, compensation);
    return response.data;
  } catch (e) {
    return e.response?.data?.message || "error";
  }
};

// The Bulk Onboard screen's candidate list - everyone in a job without active compensation yet
export const getEmployeesWithoutCompensation = async (jobId) => {
  try {
    const response = await API.get(`/employee-compensation/job/${jobId}/uncovered`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Returns { created: [...], skippedEmployeeIdsAlreadyActive: [...] }
export const bulkAssignCompensation = async (payload) => {
  try {
    const response = await API.post("/employee-compensation/bulk-assign", payload);
    return response.data;
  } catch (e) {
    return e.response?.data?.message || "error";
  }
};
//END EMPLOYEE COMPENSATION DATA

// Full compensation timeline for an employee, newest first
export const getCompensationHistory = async (employeeId) => {
  try {
    const response = await API.get(`/employee-compensation/employee/${employeeId}/history`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

//PAYROLL PERIOD DATA
export const getPayrollPeriods = async () => {
  try {
    const response = await API.get("/payroll-period");
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPayrollPeriodsByCountry = async (countryCode) => {
  try {
    const response = await API.get(`/payroll-period/country/${countryCode}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPayrollPeriodsByStatus = async (status) => {
  try {
    const response = await API.get(`/payroll-period/status/${status}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPayrollPeriod = async (id) => {
  try {
    const response = await API.get(`/payroll-period/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Always starts status "open"
export const createPayrollPeriod = async (period) => {
  try {
    const response = await API.post("/payroll-period", period);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Backend only allows this while status is "open"
export const updatePayrollPeriod = async (id, period) => {
  try {
    const response = await API.put(`/payroll-period/${id}`, period);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// "open" -> "processing"
export const processPayrollPeriod = async (id) => {
  try {
    const response = await API.patch(`/payroll-period/${id}/process`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// "open"/"processing" -> "closed"
export const closePayrollPeriod = async (id) => {
  try {
    const response = await API.patch(`/payroll-period/${id}/close`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// "closed" -> "locked" - PERMANENT, no transition back
export const lockPayrollPeriod = async (id) => {
  try {
    const response = await API.patch(`/payroll-period/${id}/lock`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// Only allowed while "open" and with no data against it
export const deletePayrollPeriod = async (id) => {
  try {
    const response = await API.delete(`/payroll-period/${id}`);
    return response.data;
  } catch (e) {
    console.error(`Error deleting payroll period with ID ${id}:`, e);
    return "error";
  }
};
//END PAYROLL PERIOD DATA

//PAYROLL RUN DATA
export const getPayrollRunsByPeriod = async (periodId) => {
  try {
    const response = await API.get(`/payroll-run/period/${periodId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const createPayrollRun = async (run) => {
  try {
    const response = await API.post("/payroll-run", run);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const calculatePayrollRun = async (id) => {
  try {
    const response = await API.post(`/payroll-run/${id}/calculate`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deletePayrollRun = async (id) => {
  try {
    const response = await API.delete(`/payroll-run/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};
//END PAYROLL RUN DATA

// PAYROLL INPUT DATA
export const getPayrollInputsForEmployeePeriod = async (periodId, employeeId) => {
  try {
    const response = await API.get(`/payroll-input/period/${periodId}/employee/${employeeId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const addPayrollInput = async (input) => {
  try {
    const response = await API.post("/payroll-input", input);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const generateInputsFromTemplate = async (employeeId, periodId, templateId) => {
  try {
    const response = await API.post(
      `/payroll-input/generate-from-template/${employeeId}/${periodId}/${templateId}`
    );
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const updatePayrollInput = async (id, input) => {
  try {
    const response = await API.put(`/payroll-input/${id}`, input);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const submitPayrollInput = async (id) => {
  try {
    const response = await API.patch(`/payroll-input/${id}/submit`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const approvePayrollInput = async (id, approverEmployeeId) => {
  try {
    const response = await API.patch(`/payroll-input/${id}/approve/${approverEmployeeId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const deletePayrollInput = async (id) => {
  try {
    const response = await API.delete(`/payroll-input/${id}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// END PAYROLL INPUT DATA

//PAYROLL RUN LIFECYCLE + RESULTS DATA
export const getPayrollRunEmployees = async (runId) => {
  try {
    const response = await API.get(`/payroll-run-employee/run/${runId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const getPayrollResultsByRun = async (runId) => {
  try {
    const response = await API.get(`/payroll-result/run/${runId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// "calculated" -> "approved" (the approval gate)
export const approvePayrollRun = async (id, approverEmployeeId) => {
  try {
    const response = await API.patch(`/payroll-run/${id}/approve/${approverEmployeeId}`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// "calculated" -> "draft", with a reason (sent as plain text body)
export const rejectPayrollRun = async (id, reason) => {
  try {
    const response = await API.patch(`/payroll-run/${id}/reject`, reason, {
      headers: {
        "Content-Type": "text/plain",
      },
    });
    return response.data;
  } catch (e) {
    return "error";
  }
};

// "approved" -> "posted"
export const postPayrollRun = async (id) => {
  try {
    const response = await API.patch(`/payroll-run/${id}/post`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

// "posted" -> "paid" (final)
export const payPayrollRun = async (id) => {
  try {
    const response = await API.patch(`/payroll-run/${id}/pay`);
    return response.data;
  } catch (e) {
    return "error";
  }
};

export const populateRunEmployees = async (runId) => {
  try {
    const response = await API.post(`/payroll-run-employee/run/${runId}/populate-active`);
    return response.data;
  } catch (e) {
    return "error";
  }
};
//END PAYROLL RUN LIFECYCLE + RESULTS DATA

export const getCompanyPerformanceTrend = async (year, employeeId, isQuarterly, companyId) => {
  try {
    const response = await API.get("/kpi_assignments/company-trend", {
      params: { year, employeeId, isQuarterly, companyId },
    });
    return response.data; // returns [{ period, year, averageScore }]
  } catch (e) {
    console.error("Error fetching company performance trend:", e);
    return "error";
  }
};

export default API;
